import { Box, TextField, Typography } from '@mui/material';
import Modal from 'shared/Modal';
import { RequiredProvisionModalProps } from './types';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { IntegrationAgentsNameSelector } from 'TenantModule/components/Management/IntegrationAgentsNameSelector';
import { IntegrationAgentNames, integrationAgents } from '@constants/integration';
import { StyledDefaultPrimaryButton } from 'styles';
import { useCreateBakIntegration } from 'IntegrationModule/components/ProvisionModal/hooks/useCreateBakIntegration';
import { selectTeamHash } from 'TeamModule/selectors';
import SearchableTenantSelect from 'TeamModule/components/Filters/SearchableTenantSelect';
import SearchableRealmSelect from 'TeamModule/components/Filters/SearchableRealmSelect';
import { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import useSearchTeamsByFieldsQuery from 'TeamModule/query/hooks/useSearchTeamsByFieldsQuery';
import ApiClientManager from 'core/apiClient';

// only apply to Ajera, BST and Vision
const bakIntegrationItems = integrationAgents.filter((agent) =>
  [
    IntegrationAgentNames.DeltekAjeraAgent,
    IntegrationAgentNames.BstAgent,
    IntegrationAgentNames.DeltekVisionAgent,
  ].includes(agent.value),
);

export const BakIntegrationProvisionModal: React.FC<RequiredProvisionModalProps> = ({
  teamId,
  toggle,
}) => {
  const teamHash = useSelector(selectTeamHash);
  const teamName = teamHash[teamId]?.name || '';

  // Get auth default tenant and realm context to ensure we fetch the correct team
  const currentTenant = ApiClientManager.getAuthDefaultTenant();
  const currentRealm = ApiClientManager.getAuthDefaultRealm();

  // Fetch team data to get default tenant and realm
  // Include tenant and realmId to ensure unique identification (teamId alone is not globally unique)
  const { teamsSearch } = useSearchTeamsByFieldsQuery({
    tenant: currentTenant?.tenantName,
    realmId: currentRealm?.realm_id,
    teamId: teamId?.toString(),
    enabled: !!teamId && !!currentTenant?.tenantName && !!currentRealm?.realm_id,
    limit: 1,
  });

  const teamData = teamsSearch?.data?.[0];

  // Tenant/realm selection state - track user selections separately from defaults
  // null = user hasn't made a selection yet, use default from teamData
  // string = user has explicitly selected a value
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [selectedRealmId, setSelectedRealmId] = useState<string | null>(null);

  // Compute display values: use user selection if made, otherwise use team data defaults
  const displayTenant = selectedTenant ?? teamData?.tenant ?? '';
  const displayRealmId = selectedRealmId ?? teamData?.realmId ?? '';

  const handleTenantChange = (tenant: string) => {
    setSelectedTenant(tenant);
    // Reset realm when tenant changes since realms are tenant-specific
    setSelectedRealmId('');
  };

  const handleRealmChange = (realmId: string) => {
    // Only set realm selection, don't affect tenant default loading
    setSelectedRealmId(realmId);
  };

  const [bakIntegrationParams, setBakIntegrationParams] = useState<{
    teamName: string;
    targetServiceId: Nullable<number>;
    s3BucketName: string;
    integrationAgentName: Nullable<IntegrationAgentNames>;
  }>({
    teamName: teamName,
    targetServiceId: null,
    s3BucketName: '',
    integrationAgentName: null,
  });
  const { createBakIntegration, isCreatingBakIntegration } = useCreateBakIntegration();

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const { integrationAgentName, teamName, s3BucketName, targetServiceId } = bakIntegrationParams;

    const realmIdToUse = displayRealmId;

    const tenantToUse = displayTenant;

    if (integrationAgentName && realmIdToUse && targetServiceId && tenantToUse) {
      createBakIntegration({
        teamName,
        integrationId: targetServiceId,
        realmId: realmIdToUse,
        s3BucketName,
        serviceName: integrationAgentName,
        tenant: tenantToUse, // Pass tenant for correct API headers
      });
    }
  };

  const onBakIntegrationValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setBakIntegrationParams((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Modal modalWidth={600} open onClose={isCreatingBakIntegration ? undefined : toggle}>
      <Box>
        <h3>Provision .Bak Integration</h3>
        <form onSubmit={onSubmit}>
          <Box
            display={'flex'}
            flexDirection={'column'}
            gap={2}
            justifyContent={'flex-start'}
            alignItems={'stretch'}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Organization
            </Typography>
            <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={2}>
              <SearchableTenantSelect
                value={displayTenant}
                onChange={handleTenantChange}
                label="Tenant"
                labelId="bak-provision-tenant-select"
                hideAllOption
              />
              <SearchableRealmSelect
                tenantName={displayTenant}
                value={displayRealmId}
                onChange={handleRealmChange}
                label="Realm ID"
                labelId="bak-provision-realm-select"
                isoId={SandboxIsoStateIds.AppbarRealmSelector}
                hideAllOption
              />
            </Box>

            <TextField
              label="Integration ID"
              type="number"
              name="targetServiceId"
              value={bakIntegrationParams.targetServiceId}
              onChange={onBakIntegrationValueChange}
              required
            />
            <IntegrationAgentsNameSelector
              value={bakIntegrationParams.integrationAgentName || ''}
              onChange={onBakIntegrationValueChange}
              disabled={isCreatingBakIntegration}
              itemsOverride={bakIntegrationItems}
            />
            <TextField
              label="Bucket Name"
              name="s3BucketName"
              disabled={isCreatingBakIntegration}
              value={bakIntegrationParams.s3BucketName}
              onChange={onBakIntegrationValueChange}
              required
            />
          </Box>
          <StyledDefaultPrimaryButton type="submit" disabled={isCreatingBakIntegration}>
            Provision
          </StyledDefaultPrimaryButton>
        </form>
      </Box>
    </Modal>
  );
};
